import { useMemo } from 'react';
import { useAuthStore } from '@/stores/auth/auth.store';
import { useOnboardingStore } from '@/stores/onboarding/onboarding.store';
import { AuthStatus, type AuthStatusResult } from './useAuthStatus.types';

export function useAuthStatus(): AuthStatusResult {
  // Get state from stores
  const onboardingStore = useOnboardingStore();
  const authStore = useAuthStore();

  const authStatusResult = useMemo((): AuthStatusResult => {
    // Check if user has keypair (session)
    const hasKeypair = authStore.session !== null;

    // Check if user has profile data
    const hasProfile = authStore.hasProfile;

    // On page reload sessionExport (serialized credentials in localStorage) is restored
    // before session (live auth object) is recreated. This flag prevents premature
    // redirects by keeping isLoading true until session restoration is completed.
    const isSessionRestorePending =
      Boolean(authStore.sessionExport || authStore.storedSessionId) && authStore.session === null;

    // A restored session can carry an undetermined profile (`hasProfile: null`, persisted by a
    // reload before the profile check completed) until the controller resolves it with the
    // homeserver. Keeping the app loading here stops the landing page from rendering behind the
    // signed-in header (issue #2070).
    const isProfileUndetermined = hasKeypair && hasProfile === null && authStore.isResolvingProfile;

    const isLoading =
      !onboardingStore.hasHydrated ||
      !authStore.hasHydrated ||
      authStore.isRestoringSession ||
      isSessionRestorePending ||
      isProfileUndetermined;

    // Determine the authentication status
    let status: AuthStatus;

    // User has session but explicitly no profile - needs to complete onboarding submitting the profile.json
    if (hasKeypair && hasProfile === false) {
      status = AuthStatus.NEEDS_PROFILE_CREATION;
    }
    // User has profile - fully authenticated
    else if (hasKeypair && hasProfile === true) {
      status = AuthStatus.AUTHENTICATED;
    }
    // No session, or a profile an interactive sign-in is still determining (that flow owns the
    // screen and reports its own progress) - unauthenticated
    else {
      status = AuthStatus.UNAUTHENTICATED;
    }

    return {
      status,
      isLoading,
      hasKeypair,
      hasProfile,
      isFullyAuthenticated: status === AuthStatus.AUTHENTICATED,
    };
  }, [
    onboardingStore.hasHydrated,
    authStore.hasHydrated,
    authStore.isRestoringSession,
    authStore.isResolvingProfile,
    authStore.sessionExport,
    authStore.storedSessionId,
    authStore.session,
    authStore.hasProfile,
  ]);

  return authStatusResult;
}
